import React, {
  useCallback, useEffect, useRef, useState, FunctionComponent,
} from 'react';
import { Box, Flex, Text } from 'rebass/styled-components';
import { useAppState, useNotification } from '../../hooks';
import api from '../../api';
import UserIntegrationCredentialsButton from './user-integration-credentials-button';
import UserIntegrationCredentialsBox from './user-integration-credentials-box';
import { Link } from '..';

const copyText = require('./copy-text.json');

const { USER_INTEGRATION_CREDENTIALS_INFO_TITLE } = copyText;

const UserIntegrationCredentials: FunctionComponent<{}> = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoadingUsername, setLoadingUsername] = useState<boolean>(false);
  const [isLoadingPassword, setLoadingPassword] = useState<boolean>(false);
  const [isLoadingCreate, setLoadingCreate] = useState<boolean>(false);
  const { accountId } = useAppState();
  const isMounted = useRef<boolean>(false);
  const { addNotification } = useNotification();

  const fetchUserIntegrationUsername = useCallback(
    async () => {
      try {
        if (isMounted.current) {
          setLoadingUsername(true);
        }
        const res = await api.ccsV2.getUserIntegrationUsername(accountId);
        if (isMounted.current) {
          setUsername(res);
          setLoadingUsername(false);
        }
        return true;
      } catch (err) {
        if (isMounted.current) {
          setUsername('');
          setLoadingUsername(false);
        }
        return false;
      }
    }, [accountId],
  );

  const fetchUserIntegrationPassword = useCallback(
    async () => {
      try {
        if (isMounted.current) {
          setLoadingPassword(true);
        }
        const res = await api.ccsV2.getUserIntegrationPassword(accountId);
        if (isMounted.current) {
          setPassword(res);
        }
      } catch (err) {
        if (isMounted.current) {
          setPassword('');
        }
      } finally {
        if (isMounted.current) {
          setLoadingPassword(false);
        }
      }
    }, [accountId],
  );

  const createUserIntegrationAccount = useCallback(
    async () => {
      try {
        if (isMounted.current) {
          setLoadingCreate(true);
        }
        const res = await api.ccsV2.initUserIntegrationAccount(accountId);
        if (isMounted.current) {
          setUsername(res.username);
          setPassword(res.password);
        }
      } finally {
        if (isMounted.current) {
          setLoadingCreate(false);
        }
      }
    }, [accountId],
  );

  const recycleUserIntegrationAccount = useCallback(
    async () => {
      try {
        if (isMounted.current) {
          setLoadingCreate(true);
        }
        const res = await api.ccsV2.recycleUserIntegrationAccount(accountId, username);
        if (isMounted.current) {
          setUsername(res.username);
          setPassword(res.password);
          addNotification('Your credentials have been reset', 'success');
        }
      } catch (err) {
        addNotification(err, 'error');
      } finally {
        if (isMounted.current) {
          setLoadingCreate(false);
        }
      }
    }, [accountId, username, addNotification],
  );

  const initUserIntegrationCredentials = useCallback(
    async () => {
      try {
        // always have an account here
        if (!await fetchUserIntegrationUsername()) {
          createUserIntegrationAccount();
        }
      } catch (err) {
        // if we have an error trying to fetch the account, do not create
        // a new one, as this will invalidate existing integrations
        addNotification(err, 'error');
      }
    }, [addNotification, createUserIntegrationAccount, fetchUserIntegrationUsername],
  );

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (accountId) {
      initUserIntegrationCredentials();
    }
  }, [accountId, initUserIntegrationCredentials]);

  return (
    <Box>
      <Flex flexDirection="column">
        <Text variant="p1" mt={2}>
          {USER_INTEGRATION_CREDENTIALS_INFO_TITLE}
        </Text>
        <Box mt={5}>
          <Box>
            <UserIntegrationCredentialsBox
              username={username}
              password={password}
              hidePassword={() => {
                setPassword('');
              }}
              isLoadingCreate={isLoadingCreate}
              isLoadingPassword={isLoadingPassword}
              onClickShowPassword={fetchUserIntegrationPassword}
            />
          </Box>

          <Box mt={5}>
            <UserIntegrationCredentialsButton
              username={username}
              isLoadingUsername={isLoadingUsername}
              isLoadingCreate={isLoadingCreate}
              onClickCreateIntegrationUser={createUserIntegrationAccount}
              recycleIntegrationUser={recycleUserIntegrationAccount}
            />
          </Box>
        </Box>
      </Flex>
    </Box>
  );
};

export default UserIntegrationCredentials;
